import type { ReactNode } from 'react';
import { Callout } from '@humanforce/mango-ui-react';

type CalloutProps = Parameters<typeof Callout>[0];

interface PayRunInlineCalloutProps {
  tone: CalloutProps['tone'];
  title: ReactNode;
  /** Runs on the title's own line at regular weight, as the design draws these banners. */
  body?: ReactNode;
  /** Sits after the body on the same line, e.g. a readiness hint. */
  adornment?: ReactNode;
  action?: CalloutProps['action'];
  testId?: string;
}

/**
 * A status banner that stays on one full-width row.
 *
 * Mango's Callout stacks `title` above `children` and adds a margin between them, so a two-part
 * message always costs two lines. The design gives these banners a single line, so the whole
 * sentence is passed as the title with the body inline at regular weight — no `children` means no
 * `--has-message`, and so no second line.
 *
 * This is for short status copy. A long explanation still belongs in a plain Callout, where
 * wrapping is the right behaviour rather than a layout failure.
 */
export const PayRunInlineCallout = ({
  tone,
  title,
  body,
  adornment,
  action,
  testId,
}: PayRunInlineCalloutProps) => (
  <Callout
    tone={tone}
    testId={testId}
    action={action}
    title={(
      <span className="mgo-cluster mgo-gap-xs mgo-align-center">
        <span>{title}</span>
        {body && <span className="mgo-weight-regular">{body}</span>}
        {adornment}
      </span>
    )}
  />
);
